import { Button } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { SimpleLoader } from "../Layout/Loader";
import DashboardTableScroll from "../shared/DashboardTableScroll";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import { AiOutlineArrowRight } from "react-icons/ai";
import { useNotifications } from "../../hooks/useNotifications";
import { useMarkNotificationsReadOnPage } from "../../hooks/useMarkNotificationsReadOnPage";
import {
  orderBelongsInSellerDisputesList,
  getOrderListStatusLabel,
} from "../../utils/orderListStatus";
import { SELLER_REFUND_REVIEW_TYPES, VA_MEDIATION_NOTIFICATION_TYPES } from "../../utils/notificationTypes";
import {
  getNotificationRelatedOrderId,
  isSellerFacingRefundNotification,
} from "../../utils/orderNotificationMatch";

const MAX_GRID_ROWS = 200;

const SELLER_DISPUTE_NOTIF_TYPES = new Set([
  ...SELLER_REFUND_REVIEW_TYPES,
  ...VA_MEDIATION_NOTIFICATION_TYPES,
]);

// Currency formatter for GBP (match RefundDetails)
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "£0.00";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(value));
};

const AllRefundOrders = () => {
    const navigate = useNavigate();
    const { shopOrders, isLoading } = useSelector((state) => state.order);
    const { user } = useSelector((state) => state.user);
    const { notifications } = useNotifications();
    useMarkNotificationsReadOnPage("seller_refunds_list");
    const dispatch = useDispatch();

    const [sellerFetchSent, setSellerFetchSent] = useState(false);
    const [gridReady, setGridReady] = useState(false);
    const prevUserIdRef = useRef(null);
    const isFetchingRef = useRef(false);
    
    useEffect(() => {
        const id = requestAnimationFrame(() => setGridReady(true));
        return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        const currentUserId = user?._id;
        const isSeller = user?.isSeller;
        
        // Only fetch if user ID changed, user is a seller, and not already fetching
        if (!currentUserId || !isSeller || currentUserId === prevUserIdRef.current || isFetchingRef.current) {
            return;
        }
        prevUserIdRef.current = currentUserId;
        isFetchingRef.current = true;
        setSellerFetchSent(true);
        dispatch(getAllOrdersOfShop(currentUserId)).finally(() => {
            isFetchingRef.current = false;
        });
    }, [dispatch, user?._id, user?.isSeller]);

    // Memoize notification counts to prevent unnecessary re-renders
    const notificationCounts = useMemo(() => {
        const counts = {};
        if (!Array.isArray(notifications)) return counts;
        notifications.forEach((notif) => {
            if (notif.read || !SELLER_DISPUTE_NOTIF_TYPES.has(notif.type)) return;
            if (!isSellerFacingRefundNotification(notif)) return;
            const orderId = getNotificationRelatedOrderId(notif);
            if (orderId) {
                counts[orderId] = (counts[orderId] || 0) + 1;
            }
        });
        return counts;
    }, [notifications]);

    const refundOrders = useMemo(() => {
        if (!shopOrders) return [];
        const byId = new Map();
        shopOrders.forEach((order) => {
            if (orderBelongsInSellerDisputesList(order)) {
                byId.set(String(order._id), order);
            }
        });
        Object.keys(notificationCounts).forEach((orderId) => {
            if (byId.has(orderId)) return;
            const order = shopOrders.find((o) => String(o._id) === orderId);
            if (order) byId.set(orderId, order);
        });
        return [...byId.values()];
    }, [shopOrders, notificationCounts]);

    const columns = useMemo(() => [
        {
            field: "createdAt",
            headerName: "Date",
            minWidth: 150,
            flex: 0.8,
            valueFormatter: (params) => {
                if (!params.value) return "";
                try {
                    return new Date(params.value).toLocaleDateString();
                } catch {
                    return "";
                }
            },
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 150,
            flex: 0.8,
            renderCell: (params) => {
                const unreadCount = params.row.unreadCount ?? 0;
                
                const status = params.value || "";
                const statusColors = {
                    "Processing refund": "bg-yellow-100 text-yellow-800",
                    "Return Required": "bg-amber-100 text-amber-800",
                    "Return in Transit": "bg-blue-100 text-blue-800",
                    "Refund Awaiting Seller Confirmation": "bg-amber-100 text-amber-800",
                    "Refund Success": "bg-green-100 text-green-800",
                    "Refund Rejected": "bg-red-100 text-red-800",
                    "Refund Resolved": "bg-indigo-100 text-indigo-800",
                    Delivered: "bg-green-100 text-green-800",
                };
                const statusLabel =
                    status === "Refund Success"
                        ? "Refund credited"
                        : status === "Refund Rejected"
                        ? "Declined"
                        : status === "Refund Resolved"
                        ? "Resolved (no refund)"
                        : status === "Processing refund"
                        ? "Under review"
                        : status === "Return Required"
                        ? "Return required"
                        : status === "Return in Transit"
                        ? "Return in transit"
                        : status === "Refund Awaiting Seller Confirmation"
                        ? "Awaiting seller confirmation"
                        : status;
                const colorClass = statusColors[status] || "bg-gray-100 text-gray-800";
                return (
                    <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${colorClass}`}>
                            {statusLabel}
                        </span>
                        {unreadCount > 0 && (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
                                {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            field: "itemsQty",
            headerName: "Items Qty",
            type: "number",
            minWidth: 130,
            flex: 0.7,
        },

        {
            field: "total",
            headerName: "Total",
            type: "number",
            minWidth: 130,
            flex: 0.8,
        },

        {
            field: "actions",
            flex: 1,
            minWidth: 150,
            headerName: "",
            sortable: false,
            renderCell: (params) => {
                return (
                    <>
                        <Link to={`/profile/disputes-refunds?tab=review-refunds&order=${params.id}`}>
                            <Button>
                                <AiOutlineArrowRight size={20} />
                            </Button>
                        </Link>
                    </>
                );
            },
        },
    ], []);

    // Effective status: when all product refunds are rejected/success, show that even if order.status is stale
    const getEffectiveStatus = (order) => {
        if (!order?.refunds?.length) return order?.status;
        const allRejected = order.refunds.every((r) => r.status === "Refund Rejected");
        const allSuccess = order.refunds.every((r) => r.status === "Refund Success");
        const allResolved = order.refunds.every((r) => r.status === "Refund Resolved");
        const hasAwaitingSellerConfirmation = order.refunds.some(
            (r) => r.status === "Refund Awaiting Seller Confirmation"
        );
        const hasTransit = order.refunds.some((r) => r.status === "Return in Transit");
        const hasRequired = order.refunds.some((r) => r.status === "Return Required");
        const hasOfferPending = order.refunds.some((r) => r.status === "Refund Offer Pending");
        if (allRejected) return "Refund Rejected";
        if (allSuccess) return "Refund Success";
        if (allResolved) return "Refund Resolved";
        if (hasOfferPending) return "Refund Offer Pending";
        if (hasAwaitingSellerConfirmation) return "Refund Awaiting Seller Confirmation";
        if (hasTransit) return "Return in Transit";
        if (hasRequired) return "Return Required";
        return order.status;
    };

    const row = useMemo(() => {
        if (!gridReady || !refundOrders || !Array.isArray(refundOrders)) return [];
        const list = [...refundOrders.slice(0, MAX_GRID_ROWS)].sort((a, b) => {
            const aUnread = notificationCounts[a._id?.toString()] || 0;
            const bUnread = notificationCounts[b._id?.toString()] || 0;
            if (bUnread !== aUnread) return bUnread - aUnread;
            return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
        });
        return list.map((item) => {
            const id = item._id?.toString();
            const status = getEffectiveStatus(item);
            return {
                id: item._id,
                createdAt: item.createdAt || new Date(),
                itemsQty: item.cart?.length || 0,
                total: formatCurrency(item.totalPrice ?? 0),
                status,
                statusLabel: getOrderListStatusLabel(item) || status,
                unreadCount: notificationCounts[id] || 0,
            };
        });
    }, [refundOrders, notificationCounts, gridReady]);

    // Memoize handleRowClick to prevent unnecessary re-renders
    const handleRowClick = useCallback((params) => {
        navigate(`/profile/disputes-refunds?tab=review-refunds&order=${params.id}`);
    }, [navigate]);

    const showLoader = !sellerFetchSent || isLoading || !gridReady;
    if (showLoader) {
        return <SimpleLoader />;
    }
    return (
        <>
                <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                        <h2 className="text-xl font-semibold text-slate-900">Buyer Refunds</h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Review and process refund requests from buyers.
                        </p>
                    </div>
                    <div className="p-4">
                      <DashboardTableScroll>
                        <div className="min-w-[720px]">
                          <DataGrid
                            rows={row}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            disableSelectionOnClick
                            autoHeight
                            initialState={{ sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] } }}
                            onRowClick={handleRowClick}
                            sx={{
                              border: "none",
                              "& .MuiDataGrid-cell": {
                                borderBottom: "1px solid #e2e8f0",
                              },
                              "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#1e293b",
                                borderBottom: "2px solid #334155",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                                color: "#ffffff",
                                "& .MuiDataGrid-columnHeaderTitle": {
                                  fontWeight: 700,
                                  fontSize: "0.875rem",
                                  color: "#ffffff",
                                },
                              },
                              "& .MuiDataGrid-row": {
                                cursor: "pointer",
                              },
                              "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f8fafc",
                              },
                            }}
                          />
                        </div>
                      </DashboardTableScroll>
                    </div>
                </div>
        </>
    );
};

export default AllRefundOrders;